<template>
  <template v-if="!isLoading">
    <div class="rounded-lg shadow-md border-l border-r border-t border-grey-light-200 mt-0 bg-white">
      <div class="p-4 flex flex-col md:flex-row gap-4">
        <Button @click="onGoBack" variant="outline">
          <ArrowLeft class="h-4 w-4" />
        </Button>
        <div>
          <h2 class="text-xl text-black font-semibold">Invoice Report</h2>
          <p class="text-grey-light-600">Reference: {{ invoice?.reference }}</p>
          <p v-if="errorMsg !== ''" class="text-sm font-medium text-destructive">{{ errorMsg }}</p>
        </div>
        <div class="flex gap-2 md:ml-auto">
          <Button @click="downloadDocument" variant="outline" :disabled="downloading">
            <Download v-if="!downloading" class="h-4 w-4" />
            <LayupSpinner v-else :siz="4" />
          </Button>
        </div>
      </div>
      <div class="border-t border-grey-light-200">
        <div
          class="grid grid-cols-1 gap-4 p-4"
          :class="{ 'md:grid-cols-4': invoice?.status, 'md:grid-cols-3': !invoice?.status }"
        >
          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Created Date</p>
            <p class="text-brand-700 text-xl font-semibold">{{ displayDate(invoice?.createdAt) }}</p>
          </div>
          <div class="col-span-1" v-if="invoice?.status">
            <p class="text-grey-light-600 text-sm font-medium">Status</p>
            <Badge :variant="getInvoiceStatusVariant(invoice?.status)">{{ invoice?.status }}</Badge>
          </div>
          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Amount excl VAT</p>
            <p class="text-brand-700 text-xl font-semibold">{{ currency(invoice?.exVatAmount || 0) }}</p>
          </div>
          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Total Amount</p>
            <p class="text-brand-700 text-xl font-extrabold">{{ currency(invoice?.totalAmount || 0) }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <div class="rounded-lg border border-gray-200 overflow-hidden bg-white">
        <div class="p-6">
          <h2 class="text-lg font-semibold text-gray-900">Fee Summary</h2>
        </div>
        <div class="border-t border-gray-200">
          <table class="w-full table-auto text-sm text-left">
            <thead class="bg-grey-light-100 text-grey-light-600 font-medium border-b border-gray-200">
              <tr>
                <th class="px-4 py-2">Item</th>
                <th class="px-4 py-2 text-right">Fees - Excl VAT</th>
                <th class="px-4 py-2 text-right">VAT</th>
                <th class="px-4 py-2 text-right">Total Fees - Incl VAT</th>
              </tr>
            </thead>
            <tbody class="text-grey-light-600">
              <template v-if="invoice?.report?.fees?.length && invoice?.report?.fees?.length > 0">
                <tr v-for="fee in invoice?.report?.fees" :key="fee.id" class="border-b border-gray-200">
                  <td class="p-5">{{ fee.productName }}</td>
                  <td class="p-5 text-right">{{ currency(fee.amountExcludingVAT) }}</td>
                  <td class="p-5 text-right">{{ currency(fee.amountVAT) }}</td>
                  <td class="p-5 text-right">{{ currency(fee.amountTotal) }}</td>
                </tr>
              </template>
              <tr>
                <td class="p-5 text-gray-900 font-semibold">Total Amount</td>
                <td class="p-5 text-gray-900 font-semibold text-right">
                  {{ currency(invoice?.report?.totalFeesExtAmount || 0) }}
                </td>
                <td class="p-5 text-gray-900 font-semibold text-right">
                  {{ currency(invoice?.report?.totalAmountVAT) }}
                </td>
                <td class="p-5 text-gray-900 font-semibold text-right">
                  {{ currency(invoice?.report?.totalFeesAmount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
  <div class="flex justify-center">
    <LayupSpinner :size="10" v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { downloadInvoicePdf, getInvoiceById } from '@/services/invoices';
import { currency, displayDate } from '@/utils/format.utils';
import { getInvoiceStatusVariant } from '@/utils/invoice.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ArrowLeft, Download } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelInvoiceReport } from '@layup/api-sdk';

const router = useRouter();
const route = useRoute();
const id = route.params.id as string;

const isLoading = ref(true);
const downloading = ref(false);
const errorMsg = ref('');
const invoice = ref<ModelInvoiceReport>();

onMounted(async () => await loadInvoice());

const loadInvoice = async () => {
  try {
    isLoading.value = true;

    if (!id) {
      router.push({ name: 'merchantInvoices' });
    }

    const invoiceData = await getInvoiceById(id);
    invoice.value = invoiceData;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details', { status: 'network_error', channel: 'merchant', method: 'loadInvoice' });
    } else {
      createException(error);
      addMetric('invoice-details', { status: 'error', channel: 'merchant', method: 'loadInvoice' });
    }
  } finally {
    isLoading.value = false;
  }
};

const downloadDocument = async () => {
  try {
    if (!id) {
      errorMsg.value = 'No invoice ID found. Please contact support.';
      return;
    }

    const url = await downloadInvoicePdf(id);

    if (!url.redirectUrl) {
      errorMsg.value = 'No download URL found. Please contact support.';
      return;
    }

    const link = document.createElement('a');
    link.href = url.redirectUrl;
    link.target = '_blank';
    link.setAttribute('download', `${id}-${invoice.value?.reference}-invoice-report.pdf`);
    link.click();

    addMetric('invoice-details-pdf-download', { status: 'success', channel: 'merchant' });
  } catch (error: any) {
    errorMsg.value = error.response?.data ?? 'Something went wrong';

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details-pdf-download', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('invoice-details-pdf-download', { status: 'error', channel: 'merchant' });
    }
  }
};

const onGoBack = () => {
  router.push({ name: 'merchantInvoices' });
};
</script>
